/**
 * @file useRevealActive.ts
 * @description Сдвиг горизонтально прокручиваемого ряда к активному элементу
 * (phase19.16 — вынесено из `Tabs`; SB122 — переиспользует
 * оглавление групп настроек `SettingsToc`).
 *
 * Если выбранный элемент за краем ряда (узкая панель конструктора, выбор по
 * адресу), ряд сдвигается к нему с запасом на затухание края — только свой
 * `scrollLeft`, без `scrollIntoView`, чтобы не двигать страницу (урок
 * OrderChainStepper; замечание владельца 2026-10-11).
 *
 * ```ts
 * useRevealActive(fade.ref, activeIndex, "[role='tab']", fade.update);
 * ```
 */

import { useEffect, type RefObject } from "react";

/** Запас до края: затухание не должно гасить выбранный элемент. */
const REVEAL_PAD = 40;

/**
 * @param rowRef ряд с `overflow-x-auto`
 * @param activeIndex номер активного элемента среди найденных `selector`; -1 — нет
 * @param selector элементы ряда в порядке DOM
 * @param onMoved вызвать после сдвига (пересчёт затухания)
 */
export function useRevealActive(
  rowRef: RefObject<HTMLElement | null>,
  activeIndex: number,
  selector: string,
  onMoved?: () => void,
): void {
  useEffect(() => {
    const row = rowRef.current;
    const item = activeIndex >= 0 ? row?.querySelectorAll<HTMLElement>(selector)[activeIndex] : undefined;
    if (!row || !item || row.scrollWidth <= row.clientWidth) return;
    // Координаты элемента внутри содержимого ряда (ряд может быть не offsetParent).
    const left = item.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
    const right = left + item.getBoundingClientRect().width;
    let next = row.scrollLeft;
    if (left - REVEAL_PAD < row.scrollLeft) next = Math.max(0, left - REVEAL_PAD);
    else if (right + REVEAL_PAD > row.scrollLeft + row.clientWidth) next = right + REVEAL_PAD - row.clientWidth;
    if (next === row.scrollLeft) return;
    row.scrollLeft = next;
    onMoved?.();
  }, [activeIndex, rowRef, selector, onMoved]);
}
